import React, { Fragment, useState } from 'react';

import useLayoutContext from '@hooks/useLayoutContext';

import { useFormContext } from 'react-hook-form';

import { Input, SeparatorLine, Button, HelperText, InputSectionGroup } from '@components';

type LoginInformationProps = {
  isMobile?: boolean;
  isChangingPassword: boolean;
  setIsChangingPassword: React.Dispatch<React.SetStateAction<boolean>>;
};

const LoginInformation = React.forwardRef<HTMLElement, LoginInformationProps>(
  function WrappedSection({ isMobile = false, isChangingPassword, setIsChangingPassword }, ref) {
    const {
      register,
      formState: { errors },
    } = useFormContext();
    const { usernameLabel, isSso } = useLayoutContext();
    const isValid = (input: string): boolean => !(errors as any)[input]?.message;

    const toggle = () => setIsChangingPassword((prev) => !prev);

    return (
      <section className="py-4 md:py-8 flex flex-col w-full" ref={ref}>
        <h2 className="text-sm md:text-xl font-bold pb2 md:pb-4">Log In Information</h2>
        <InputSectionGroup>
          <InputSectionGroup.Item>
            <div className="input-section-group__field">
              <label className="input-section-group__label" htmlFor="account-username">
                {usernameLabel}
                <br />
                (Read only)
              </label>
              <div className="input-section-group__control">
                <Input
                  className="input-section-group__inline-input"
                  id="account-username"
                  noBorder
                  type="text"
                  {...register('username')}
                  {...(isValid('username') ? {} : { valid: false })}
                  readOnly
                />
              </div>
            </div>
          </InputSectionGroup.Item>

          {!isSso && (
            <Fragment>
              <InputSectionGroup.Item>
                <div className="input-section-group__field">
                  <div className="input-section-group__label">Password</div>
                  <div className="input-section-group__control">
                    <Button
                      className="text-blue-600 font-thin"
                      layout="link"
                      size="noPadding"
                      onClick={toggle}
                    >
                      {isChangingPassword ? "Don't Change Password" : 'Change Password'}
                    </Button>
                  </div>
                </div>
              </InputSectionGroup.Item>

              {isChangingPassword && (
                <>
                  <InputSectionGroup.Item>
                    <div className="input-section-group__field">
                      <label className="input-section-group__label" htmlFor="old-password">
                        Old Password
                      </label>
                      <div className="input-section-group__control">
                        <Input
                          className="input-section-group__inline-input"
                          id="old-password"
                          noBorder
                          {...register('oldPassword')}
                          placeholder="Password"
                          type="password"
                        />
                        <HelperText valid={isValid('passwordConfirmation')}>
                          {/* @ts-ignore */}
                          {errors.oldPassword?.message}
                        </HelperText>
                      </div>
                    </div>
                  </InputSectionGroup.Item>

                  <InputSectionGroup.Item>
                    <div className="input-section-group__field">
                      <label className="input-section-group__label" htmlFor="new-password">
                        New Password
                      </label>
                      <div className="input-section-group__control">
                        <Input
                          className="input-section-group__inline-input"
                          id="new-password"
                          noBorder
                          {...register('password')}
                          placeholder="New Password"
                          type="password"
                        />
                        <HelperText valid={isValid('passwordConfirmation')}>
                          {/* @ts-ignore */}
                          {errors.password?.message}
                        </HelperText>
                      </div>
                    </div>
                  </InputSectionGroup.Item>

                  <InputSectionGroup.Item>
                    <div className="input-section-group__field">
                      <label className="input-section-group__label" htmlFor="password-confirmation">
                        Confirm New Password
                      </label>
                      <div className="input-section-group__control">
                        <Input
                          className="input-section-group__inline-input"
                          id="password-confirmation"
                          noBorder
                          {...register('passwordConfirmation')}
                          placeholder="New Password Confirmation"
                          type="password"
                        />
                        <HelperText valid={isValid('passwordConfirmation')}>
                          {/* @ts-ignore */}
                          {errors?.passwordConfirmation?.message}
                        </HelperText>
                      </div>
                    </div>
                  </InputSectionGroup.Item>
                </>
              )}
            </Fragment>
          )}
        </InputSectionGroup>

        {isMobile && <SeparatorLine />}
      </section>
    );
  },
);

export default LoginInformation;
